import React, { useState } from "react";
import { History } from "lucide-react";
import { AppButton, AppCard, AppCardContent, AppCardHeader, AppCardTitle, ROLE_LABELS } from "../../ui";
import { formatDay, formatMoment } from "./scheduleFormat";

const FIRST_SHOWN = 5;

const pluralOf = (noun) => (noun === "class" ? "classes" : `${noun}s`);

/** What one change did, in a sentence (after "Name (Role) "). */
function describe(entry, unit) {
  const noun = unit.toLowerCase();
  if (entry.kind === "carry_over") {
    const n = (entry.topics || []).length;
    return ` carried ${n} topic${n === 1 ? "" : "s"} from ${unit} ${entry.fromDay} to ${unit} ${entry.toDay}`
      + (n ? `: ${entry.topics.join(", ")}` : "");
  }
  if (entry.kind === "repack") {
    const s = entry.summary || {};
    return ` re-packed the ${pluralOf(noun)} still to teach${s.before != null ? ` — ${s.before} became ${s.after}` : ""}`;
  }
  const followers = (entry.changes || []).filter((c) => c.dayNumber !== entry.fromDay).length;
  return ` moved ${unit} ${entry.fromDay} to ${formatDay(entry.date)}${entry.mode === "single" ? " only" : ""}`
    + (followers ? ` — ${followers} later ${followers === 1 ? noun : pluralOf(noun)} moved with it` : "");
}

/**
 * ScheduleChangeLog — every change to a batch's class plan: who moved which
 * classes, carried topics over or re-packed, when, and why
 * (GET /syllabus/batch/:id/syllabus/changes, newest first).
 *
 * Props:
 *   changes — SyllabusScheduleChange rows
 *   unit    — "Day" | "Class" | "Session"
 */
const ScheduleChangeLog = ({ changes = [], unit = "Day" }) => {
  const [showAll, setShowAll] = useState(false);
  if (!changes.length) return null;
  const shown = showAll ? changes : changes.slice(0, FIRST_SHOWN);

  return (
    <AppCard>
      <AppCardHeader>
        <AppCardTitle className="tw-flex tw-items-center tw-gap-2 tw-text-sm">
          <History size={15} aria-hidden="true" /> Plan changes
        </AppCardTitle>
      </AppCardHeader>
      <AppCardContent>
        <ol className="tw-m-0 tw-flex tw-list-none tw-flex-col tw-gap-3 tw-p-0">
          {shown.map((entry) => {
            const moved = entry.changes || [];
            const who = entry.changedBy?.name || ROLE_LABELS[entry.changedBy?.role] || "Someone";
            const role = entry.changedBy?.name ? ROLE_LABELS[entry.changedBy?.role] : "";
            return (
              <li key={entry._id} className="tw-flex tw-flex-col tw-gap-1 tw-border-0 tw-border-b tw-border-solid tw-border-line tw-pb-3 last:tw-border-b-0 last:tw-pb-0">
                <div className="tw-flex tw-flex-wrap tw-items-baseline tw-justify-between tw-gap-x-3 tw-gap-y-0.5">
                  <p className="tw-m-0 tw-text-[0.8125rem] tw-text-content">
                    <span className="tw-font-semibold">{who}</span>
                    {role ? <span className="tw-text-content-muted"> ({role})</span> : null}
                    {describe(entry, unit)}.
                  </p>
                  <time dateTime={entry.createdAt} className="tw-text-xs tw-text-content-subtle">{formatMoment(entry.createdAt)}</time>
                </div>
                {entry.reason && (
                  <p className="tw-m-0 tw-text-xs tw-text-content-muted">“{entry.reason}”</p>
                )}
                {entry.partial && (
                  <p className="tw-m-0 tw-text-xs tw-text-warning-fg">
                    Some days changed while this was being saved and kept their date.
                  </p>
                )}
                {moved.length > 1 && (
                  <details className="tw-text-xs tw-text-content-muted">
                    <summary className="tw-cursor-pointer tw-select-none tw-focus-ring">Show the {moved.length} {pluralOf(unit.toLowerCase())}</summary>
                    <ul className="tw-m-0 tw-mt-1.5 tw-list-none tw-p-0">
                      {moved.map((c) => (
                        <li key={c.dayNumber} className="tw-py-0.5">
                          {unit} {c.dayNumber}: {c.from ? formatDay(c.from) : "—"} → {formatDay(c.to)}
                        </li>
                      ))}
                    </ul>
                  </details>
                )}
              </li>
            );
          })}
        </ol>
        {changes.length > FIRST_SHOWN && (
          <AppButton size="xs" variant="ghost" className="tw-mt-3" onClick={() => setShowAll((v) => !v)}>
            {showAll ? "Show fewer" : `Show all ${changes.length}`}
          </AppButton>
        )}
      </AppCardContent>
    </AppCard>
  );
};

export default ScheduleChangeLog;
